import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Tag, ArrowRight } from 'lucide-react';
import { blogAPI } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await blogAPI.getAll();
        setPosts(response.data);
      } catch (error) {
        console.error('Error fetching posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="public-page min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="public-page min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Новости и обновления
            </h1>
            <p className="text-xl text-gray-300">
              Последние новости о развитии AI-переводчика @lngaua_bot, новые функции и полезные статьи
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts Section */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="bg-gray-800 border-gray-700 h-full hover:bg-gray-750 transition-colors group cursor-pointer">
                    <Link to={`/blog/${post.id}`} className="block h-full">
                      {post.image && (
                        <div className="aspect-video overflow-hidden rounded-t-lg">
                          <img 
                            src={post.image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between text-sm text-gray-400 mb-2">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(post.created_at)}
                          </div>
                        </div>
                        <CardTitle className="text-white group-hover:text-blue-400 transition-colors">
                          {post.title}
                        </CardTitle>
                      </CardHeader>
                      
                      <CardContent className="flex-1 flex flex-col">
                        {post.excerpt && (
                          <CardDescription className="text-gray-400 mb-4 flex-1">
                            {post.excerpt}
                          </CardDescription>
                        )}
                        
                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="secondary" className="bg-gray-700 text-gray-300 text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {post.tags.length > 3 && (
                              <Badge variant="secondary" className="bg-gray-700 text-gray-300 text-xs">
                                +{post.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center text-blue-400 text-sm group-hover:text-blue-300 transition-colors">
                          Читать полностью
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Link>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-20"
            >
              <div className="text-6xl mb-6">📝</div>
              <h2 className="text-2xl font-bold text-white mb-4">Скоро здесь появятся статьи</h2>
              <p className="text-gray-400 max-w-md mx-auto">
                Мы работаем над созданием полезного контента о переводах, AI-технологиях и возможностях нашего бота
              </p>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
